import React, { useState, useEffect } from "react";
import moment from "moment";
import { handleUnauthorized } from "../../utils/apiHelper";
import { formatApiDate } from "../../utils/dateHelper";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import SuccessAlert from "../child/Alert/SuccessAlert";
import DengerAlert from "../child/Alert/DengerAlert";
import Loader from "../child/Loder";
import {
  fetchJoinGuestLists,
  fetchScannedGuestLists,
} from "../../services/joinGuestService";

const formatDateTime24 = (dateValue) =>
  dateValue ? moment(dateValue).format("DD-MM-YYYY HH:mm") : "N/A";

const getExpiryScanDateTime = (data) => {
  const scan = data?.userscanDateTime || data?.scanTime;
  if (scan) return moment(scan).add(24, "hours");
  if (data?.expiryScanTime) return moment(data.expiryScanTime);
  return null;
};

const ViewJoinedGuestListPage = () => {
  const [guestData, setGuestData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const isScannedModule = location.pathname.includes("/scannedGuest");

  useEffect(() => {
    const loadGuest = async () => {
      if (!id) {
        setGuestData(null);
        setLoading(false);
        setErrorMessage("Guest id is missing");
        return;
      }

      const fromState = location.state?.guest;
      if (fromState && String(fromState._id) === String(id)) {
        setGuestData(fromState);
        setLoading(false);
        return;
      }

      // No by-id API — resolve guest from list API
      try {
        setLoading(true);
        const listParams = {
          search: "",
          promoterId: "",
          hostId: "",
          clubId: "",
          userId: "",
          startDate: "",
          endDate: "",
          sortBy: "createdAt",
          sortOrder: "desc",
          page: 1,
          limit: 100,
        };

        const response = isScannedModule
          ? await fetchScannedGuestLists(listParams)
          : await fetchJoinGuestLists({
              ...listParams,
              status: "joined,scanned",
            });

        const list = response?.data?.data || [];
        const matched =
          list.find((item) => String(item._id) === String(id)) || null;
        setGuestData(matched);

        if (!matched) {
          setErrorMessage("Guest details not found");
          setTimeout(() => setErrorMessage(""), 3000);
        }
      } catch (err) {
        if (err?.response?.status === 401) {
          handleUnauthorized(navigate);
          return;
        }
        setErrorMessage(
          err?.response?.data?.message || "Failed to fetch guest details"
        );
        setTimeout(() => setErrorMessage(""), 3000);
      } finally {
        setLoading(false);
      }
    };

    loadGuest();
  }, [id, location.state, location.pathname, isScannedModule, navigate]);

  if (loading) return <Loader />;

  const scanEntries = Array.isArray(guestData?.scanDateTime)
    ? guestData.scanDateTime
    : guestData?.scanDateTime
      ? [{ scanTime: guestData.scanDateTime }]
      : [];

  return (
    <div className="row gy-4">
      {successMessage && (
        <SuccessAlert
          message={successMessage}
          onClose={() => setSuccessMessage("")}
        />
      )}
      {errorMessage && (
        <DengerAlert
          message={errorMessage}
          onClose={() => setErrorMessage("")}
        />
      )}
      <div className="col-md-12">
        <div className="card h-100">
          <div className="card-body p-24">
            {!guestData ? (
              <p>No data found.</p>
            ) : (
              <>
                <div className="row">
                  <div className="col-md-3 mb-3">
                    <label className="form-label fw-bold">User Name</label>
                    <div>{guestData?.userFullName || "N/A"}</div>
                  </div>
                  <div className="col-md-3 mb-3">
                    <label className="form-label fw-bold">Promoter</label>
                    <div>{guestData?.promoterFullName || "N/A"}</div>
                  </div>
                  <div className="col-md-3 mb-3">
                    <label className="form-label fw-bold">Venue Name</label>
                    <div>{guestData?.hostBusinessName || "N/A"}</div>
                  </div>
                  <div className="col-md-3 mb-3">
                    <label className="form-label fw-bold">Guest Title</label>
                    <div>{guestData?.promoterTitle || "N/A"}</div>
                  </div>
                  <div className="col-md-3 mb-3">
                    <label className="form-label fw-bold">Status</label>
                    <div className="text-capitalize">
                      {guestData?.status || "N/A"}
                    </div>
                  </div>
                  {isScannedModule ? (
                    <>
                      <div className="col-md-3 mb-3">
                        <label className="form-label fw-bold">
                          Scan Date Time
                        </label>
                        <div>
                          {formatDateTime24(
                            guestData?.userscanDateTime || guestData?.scanTime
                          )}
                        </div>
                      </div>
                      <div className="col-md-3 mb-3">
                        <label className="form-label fw-bold">
                          Expiry Scan Date Time
                        </label>
                        <div>
                          {formatDateTime24(getExpiryScanDateTime(guestData))}
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="col-md-3 mb-3">
                      <label className="form-label fw-bold">
                        Date Of Joining
                      </label>
                      <div>
                        {formatApiDate(
                          guestData?.DateOfjoining || guestData?.createdAt
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {isScannedModule && scanEntries.length > 0 && (
                  <div className="mt-3">
                    <label className="form-label fw-bold">Scan History</label>
                    <div className="table-responsive">
                      <table className="table bordered-table sm-table mb-0">
                        <thead>
                          <tr>
                            <th>#</th>
                            <th>Scan Date</th>
                            <th>Expiry Scan Date</th>
                          </tr>
                        </thead>
                        <tbody>
                          {scanEntries.map((scan, index) => (
                            <tr key={`${scan.scanTime || index}-${index}`}>
                              <td>{index + 1}</td>
                              <td>
                                {scan.scanTime
                                  ? formatDateTime24(scan.scanTime)
                                  : "N/A"}
                              </td>
                              <td>
                                {formatDateTime24(
                                  getExpiryScanDateTime({
                                    scanTime: scan.scanTime,
                                    userscanDateTime: scan.userscanDateTime,
                                    expiryScanTime: scan.expiryScanTime,
                                  })
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewJoinedGuestListPage;
